/**
 * Update Channel - Multi-tab coordination for app updates
 * Uses BroadcastChannel API to synchronize update state across tabs
 */

// Message types for update coordination
export const UPDATE_CHANNEL_NAME = 'postpartner-update-channel';

export interface UpdateChannelMessage {
  type: 'UPDATE_AVAILABLE' | 'UPDATE_APPLIED' | 'MANDATORY_UPDATE' | 'REQUEST_STATUS';
  payload?: {
    buildId?: string;
    version?: string;
    minBuildId?: string;
    timestamp?: number;
  };
}

type UpdateChannelListener = (message: UpdateChannelMessage) => void;

/**
 * Update Channel singleton for multi-tab coordination
 */
class UpdateChannel {
  private channel: BroadcastChannel | null = null;
  private listeners: Set<UpdateChannelListener> = new Set();
  private isSupported: boolean;

  constructor() {
    this.isSupported = typeof BroadcastChannel !== 'undefined';
    if (this.isSupported) {
      this.channel = new BroadcastChannel(UPDATE_CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<UpdateChannelMessage>) => {
        this.notifyListeners(event.data);
      };
    }
  }

  /**
   * Broadcast a message to all tabs
   */
  broadcast(message: UpdateChannelMessage): void {
    if (this.channel) {
      this.channel.postMessage(message);
    }
  }

  /**
   * Subscribe to update messages
   */
  subscribe(listener: UpdateChannelListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all local listeners
   */
  private notifyListeners(message: UpdateChannelMessage): void {
    this.listeners.forEach((listener) => {
      try {
        listener(message);
      } catch (error) {
        console.error('[UpdateChannel] Listener error:', error);
      }
    });
  }

  /**
   * Close the channel (cleanup)
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }
}

// Singleton instance
export const updateChannel = new UpdateChannel();

/**
 * Broadcast that an update is available
 */
export function broadcastUpdateAvailable(buildId: string, version?: string): void {
  updateChannel.broadcast({
    type: 'UPDATE_AVAILABLE',
    payload: {
      buildId,
      version,
      timestamp: Date.now(),
    },
  });
}

/**
 * Broadcast that an update was applied (tab is reloading)
 */
export function broadcastUpdateApplied(): void {
  updateChannel.broadcast({
    type: 'UPDATE_APPLIED',
    payload: {
      timestamp: Date.now(),
    },
  });
}

/**
 * Broadcast that a mandatory update is required
 */
export function broadcastMandatoryUpdate(minBuildId: string): void {
  updateChannel.broadcast({
    type: 'MANDATORY_UPDATE',
    payload: {
      minBuildId,
      timestamp: Date.now(),
    },
  });
}
